import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { 
  FileText, TrendingUp, TrendingDown, AlertTriangle, Lightbulb, 
  CheckCircle2, XCircle, ArrowUpRight, ArrowDownRight, Minus,
  Sparkles, Download, Brain, Target, Activity, Loader2
} from 'lucide-react';
import { ReportTimeframeSelector } from './ReportTimeframeSelector';
import { InsightsExport } from './InsightsExport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Metric Card Component
const MetricCard = ({ metric }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-emerald-500';
      case 'good': return 'bg-emerald-400';
      case 'warning': return 'bg-amber-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-slate-400';
    }
  };

  const getTrendIcon = () => {
    if (metric.trend?.direction === 'up') return <ArrowUpRight className="h-3 w-3" />;
    if (metric.trend?.direction === 'down') return <ArrowDownRight className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };

  const getTrendColor = () => {
    // For metrics where lower is better (like hospitalization rate)
    const lowerIsBetter = metric.name.toLowerCase().includes('hospitalization') || 
                          metric.name.toLowerCase().includes('missed') ||
                          metric.name.toLowerCase().includes('burnout') ||
                          metric.name.toLowerCase().includes('cost');
    
    if (metric.trend?.direction === 'up') {
      return lowerIsBetter ? 'text-red-600' : 'text-emerald-600';
    }
    if (metric.trend?.direction === 'down') {
      return lowerIsBetter ? 'text-emerald-600' : 'text-red-600';
    }
    return 'text-slate-500';
  };

  const formatValue = () => {
    if (metric.unit === '$') {
      return `$${metric.value.toLocaleString()}`;
    }
    if (metric.unit === '%') {
      return `${metric.value}%`;
    }
    return `${metric.value} ${metric.unit}`;
  };

  return (
    <Card className="hover:shadow-md transition-all" data-testid={`metric-card-${metric.name.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <p className="text-sm text-muted-foreground truncate flex-1">{metric.name}</p>
          <div className={`h-2.5 w-2.5 rounded-full ${getStatusColor(metric.status)}`} />
        </div>
        <p className="text-2xl font-bold">{formatValue()}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground">
            Target: {metric.unit === '$' ? `$${metric.target.toLocaleString()}` : `${metric.target}${metric.unit}`}
          </span>
          {metric.trend && (
            <div className={`flex items-center gap-1 text-xs ${getTrendColor()}`}>
              {getTrendIcon()}
              <span>{metric.trend.change > 0 ? '+' : ''}{metric.trend.change}%</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Insight Card Component
const InsightCard = ({ insight }) => {
  const typeConfig = {
    critical: { icon: XCircle, bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-l-red-500', color: 'text-red-600' },
    warning: { icon: AlertTriangle, bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-l-amber-500', color: 'text-amber-600' },
    positive: { icon: CheckCircle2, bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-l-emerald-500', color: 'text-emerald-600' },
    opportunity: { icon: Lightbulb, bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-l-blue-500', color: 'text-blue-600' },
    info: { icon: Lightbulb, bg: 'bg-slate-50 dark:bg-slate-800/20', border: 'border-l-slate-400', color: 'text-slate-600' }
  };

  const config = typeConfig[insight.type] || typeConfig.info;
  const Icon = config.icon;

  return (
    <div className={`p-4 rounded-lg border-l-4 ${config.border} ${config.bg}`} data-testid={`insight-${insight.type}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 ${config.color} shrink-0 mt-0.5`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`font-semibold text-sm ${config.color}`}>{insight.title}</h4>
            <Badge variant="outline" className="text-[10px] capitalize">{insight.type}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">{insight.description}</p>
          {insight.recommendation && (
            <p className="text-xs mt-2 text-slate-600 dark:text-slate-400 italic">
              Recommendation: {insight.recommendation}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Enhanced Report Section Component
export function EnhancedReportSection({ 
  domain = 'all',
  title = 'Enhanced Report',
  description = 'Comprehensive metrics with AI-powered insights',
  showTimeframe = true,
  showExport = true,
  defaultTimeframe = '30d',
  className
}) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [aiInsights, setAiInsights] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeframe, setTimeframe] = useState({
    preset: defaultTimeframe,
    startDate: null,
    endDate: null
  });

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (timeframe.startDate) params.append('start_date', timeframe.startDate);
      if (timeframe.endDate) params.append('end_date', timeframe.endDate);
      params.append('domain', domain);

      const response = await fetch(`${API_URL}/api/enhanced-reports/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        
        // Collect all insights from domains
        const allInsights = [];
        Object.values(data.domains || {}).forEach(d => {
          allInsights.push(...(d.insights || []));
        });
        setAiInsights(allInsights);
      } else {
        toast.error('Failed to load report data');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const generateAiInsights = async () => {
    setGeneratingInsights(true);
    try {
      const params = new URLSearchParams();
      if (timeframe.startDate) params.append('start_date', timeframe.startDate);
      if (timeframe.endDate) params.append('end_date', timeframe.endDate);
      params.append('domain', domain);

      const response = await fetch(`${API_URL}/api/enhanced-reports/generate-ai-insights?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiInsights(data.insights || []);
        toast.success('AI insights generated', {
          description: data.source === 'ai' ? 'Powered by GPT-4' : 'Using rule-based analysis'
        });
      }
    } catch (error) {
      console.error('Error generating insights:', error);
      toast.error('Failed to generate AI insights');
    } finally {
      setGeneratingInsights(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchReportData();
    }
  }, [token, timeframe]);

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
  };

  if (loading) {
    return (
      <Card className={className} data-testid="enhanced-report-loading">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-64" />
        </CardContent>
      </Card>
    );
  }

  const domains = reportData?.domains || {};
  const executiveSummary = reportData?.executive_summary || {};

  return (
    <Card className={className} data-testid="enhanced-report-section">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {title}
            </CardTitle>
            <CardDescription className="mt-1">{description}</CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {showTimeframe && (
              <ReportTimeframeSelector
                defaultValue={defaultTimeframe}
                onTimeframeChange={handleTimeframeChange}
                onRefresh={fetchReportData}
                loading={loading}
              />
            )}
          </div>
        </div>
        
        {/* Report Period Badge */}
        {reportData?.report_period && (
          <Badge variant="outline" className="mt-2 w-fit">
            Report Period: {reportData.report_period.start_date} to {reportData.report_period.end_date} ({reportData.report_period.days} days)
          </Badge>
        )}
      </CardHeader>

      <CardContent>
        {/* Executive Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className={`${executiveSummary.overall_status === 'critical' ? 'border-red-300 bg-red-50 dark:bg-red-900/10' : executiveSummary.overall_status === 'warning' ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/10' : 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/10'}`}>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">Status</p>
              <p className={`text-xl font-bold capitalize ${executiveSummary.overall_status === 'critical' ? 'text-red-600' : executiveSummary.overall_status === 'warning' ? 'text-amber-600' : 'text-emerald-600'}`}>
                {executiveSummary.overall_status || 'Good'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">Metrics Tracked</p>
              <p className="text-xl font-bold">{executiveSummary.total_metrics || 0}</p>
            </CardContent>
          </Card>
          <Card className={executiveSummary.critical_issues > 0 ? 'border-red-300' : ''}>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">Critical Issues</p>
              <p className={`text-xl font-bold ${executiveSummary.critical_issues > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {executiveSummary.critical_issues || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">Warnings</p>
              <p className={`text-xl font-bold ${executiveSummary.warnings > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {executiveSummary.warnings || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <Activity className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="metrics" data-testid="tab-metrics">
              <Target className="h-4 w-4 mr-2" />
              Metrics
            </TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends">
              <TrendingUp className="h-4 w-4 mr-2" />
              Trends
            </TabsTrigger>
            <TabsTrigger value="insights" data-testid="tab-insights">
              <Sparkles className="h-4 w-4 mr-2" />
              Insights
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-4">
            {Object.entries(domains).map(([key, domainData]) => (
              <div key={key} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">{domainData.title}</h3>
                  <Badge variant="outline">{domainData.metrics?.length || 0} metrics</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{domainData.summary}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {domainData.metrics?.map((metric, idx) => (
                    <MetricCard key={idx} metric={metric} />
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* Metrics Tab */}
          <TabsContent value="metrics" className="mt-4">
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead>Metric</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Target</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">Trend</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(domains).flatMap(([key, domainData]) =>
                    domainData.metrics?.map((metric, idx) => (
                      <TableRow key={`${key}-${idx}`}>
                        <TableCell className="font-medium">{domainData.title}</TableCell>
                        <TableCell>{metric.name}</TableCell>
                        <TableCell className="text-right">
                          {metric.unit === '$' ? `$${metric.value.toLocaleString()}` : `${metric.value}${metric.unit}`}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {metric.unit === '$' ? `$${metric.target.toLocaleString()}` : `${metric.target}${metric.unit}`}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            variant={metric.status === 'excellent' || metric.status === 'good' ? 'default' : metric.status === 'warning' ? 'secondary' : 'destructive'}
                            className="capitalize"
                          >
                            {metric.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {metric.trend?.direction === 'up' ? (
                            <ArrowUpRight className="h-4 w-4 inline text-emerald-500" />
                          ) : metric.trend?.direction === 'down' ? (
                            <ArrowDownRight className="h-4 w-4 inline text-red-500" />
                          ) : (
                            <Minus className="h-4 w-4 inline text-slate-400" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="mt-4 space-y-6">
            {Object.entries(domains).map(([key, domainData]) => {
              if (!domainData.chart_data?.length) return null;
              
              return (
                <Card key={key}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{domainData.title} Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={domainData.chart_data}>
                        <defs>
                          <linearGradient id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        {Object.keys(domainData.chart_data[0] || {})
                          .filter(k => k !== 'label')
                          .map((dataKey, i) => (
                            <Area 
                              key={dataKey}
                              type="monotone" 
                              dataKey={dataKey} 
                              name={dataKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              stroke={['#6366F1', '#10B981', '#F59E0B', '#EF4444'][i % 4]}
                              fill={`url(#gradient-${key})`}
                              fillOpacity={i === 0 ? 1 : 0}
                            />
                          ))
                        }
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">AI-Powered Insights & Recommendations</h3>
                <Badge variant="outline" className="text-xs">{aiInsights.length} insights</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={generateAiInsights}
                  disabled={generatingInsights}
                  data-testid="generate-ai-insights-btn"
                >
                  {generatingInsights ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Brain className="h-4 w-4 mr-2" />
                      Generate AI Insights
                    </>
                  )}
                </Button>
                {showExport && aiInsights.length > 0 && (
                  <InsightsExport 
                    insights={aiInsights.map(i => ({
                      title: i.title,
                      description: i.description,
                      severity: i.type,
                      category: 'Report',
                      recommendation: i.recommendation
                    }))} 
                    pageTitle={title} 
                  />
                )}
              </div>
            </div>
            
            <ScrollArea className="h-[400px]">
              <div className="space-y-3 pr-4">
                {aiInsights.length === 0 ? (
                  <div className="text-center py-12">
                    <Sparkles className="h-12 w-12 mx-auto mb-4 text-indigo-300" />
                    <h3 className="font-medium">No Insights Yet</h3>
                    <p className="text-muted-foreground mt-2">
                      Click "Generate AI Insights" to analyze your data
                    </p>
                  </div>
                ) : (
                  aiInsights.map((insight, idx) => (
                    <InsightCard key={idx} insight={insight} />
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

export default EnhancedReportSection;
